import React from 'react'
import ReactDOM from 'react-dom'
import { useSortable } from '@dnd-kit/react/sortable'

export const MANAGE_COLOURS = [
  '#58B6C3', '#3DB8A8', '#55C95D', '#8BC34A', '#C5D94E', '#F0C14A',
  '#F5A623', '#E67E22', '#E74C3C', '#E91E8C', '#C2185B', '#8968D7',
  '#7E57C2', '#5C6BC0', '#42A5F5', '#29B6F6', '#26C6DA', '#8B9CAF',
  '#78909C', '#607D8B', '#546E7A', '#3D4058', '#6D4C41', '#A1887F'
]

export const DEFAULT_COLOUR = MANAGE_COLOURS[0]

export const fieldError = (error, field) => {
  const data = error && error.response && error.response.data
  if (!data) return ''
  if (typeof data === 'string') return data
  if (data.detail) return Array.isArray(data.detail) ? data.detail[0] : data.detail
  if (field && data[field]) return Array.isArray(data[field]) ? data[field][0] : data[field]
  return ''
}

export const DeleteIcon = () => (
  <svg width='17' height='17' viewBox='0 0 17 17' fill='none' aria-hidden='true'>
    <path d='M3 4.5H14M6 4.5V2.8H11V4.5M4.5 4.5L5.1 14H11.9L12.5 4.5M7 7V11.8M10 7V11.8' stroke='currentColor' strokeWidth='1.25' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
)

export const DragHandle = () => (
  <svg className='manage-modal__drag-icon' width='12' height='18' viewBox='0 0 12 18' aria-hidden='true'>
    <circle cx='3' cy='4' r='1.2' fill='currentColor' />
    <circle cx='9' cy='4' r='1.2' fill='currentColor' />
    <circle cx='3' cy='9' r='1.2' fill='currentColor' />
    <circle cx='9' cy='9' r='1.2' fill='currentColor' />
    <circle cx='3' cy='14' r='1.2' fill='currentColor' />
    <circle cx='9' cy='14' r='1.2' fill='currentColor' />
  </svg>
)

// A manage-modal list row that dnd-kit can sort (inside a DragDropProvider). Only the
// drag handle starts a drag, so clicks on names/swatches/buttons in the row still work.
export const SortableRow = ({ id, index, className, children, ...props }) => {
  const { ref, handleRef, isDragging } = useSortable({ id, index })
  return (
    <li ref={ref} className={[className, isDragging ? 'is-dragging' : ''].filter(Boolean).join(' ')} {...props}>
      <div className='manage-modal__drag' ref={handleRef}>
        <DragHandle />
      </div>
      {children}
    </li>
  )
}

export const PencilIcon = () => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none' aria-hidden='true'>
    <path d='M8.3 2.1L11.9 5.7L5.2 12.4H1.6V8.8L8.3 2.1ZM9.4 1L11.1 0C11.5 -0.3 12.1 -0.2 12.5 0.2L13.8 1.5C14.2 1.9 14.3 2.5 14 2.9L12.3 4.6L9.4 1Z' fill='currentColor' />
  </svg>
)

export const ColourSwatch = ({ color, colorType = 'fill', onClick, ariaLabel }) => (
  <button
    type='button'
    className={`manage-modal__status-colour ${colorType === 'outline' ? 'manage-modal__status-colour--outline' : ''}`}
    style={colorType === 'outline'
      ? { borderColor: color, backgroundColor: 'transparent' }
      : { backgroundColor: color }}
    onClick={onClick}
    aria-label={ariaLabel || 'Choose colour'}
  />
)

// Moved to components/common so it can be used outside the manage modals; re-exported for existing imports.
export { default as ConfirmDialog } from '../../common/ConfirmDialog.jsx'

export const ColourPicker = ({
  selected,
  onSelect,
  title = 'Choose status colour',
  note = null
}) => {
  const anchorRef = React.useRef(null)
  const pickerRef = React.useRef(null)
  const [position, setPosition] = React.useState({ top: 0, left: 0, visible: false })

  React.useLayoutEffect(() => {
    const updatePosition = () => {
      if (!anchorRef.current || !anchorRef.current.parentElement) return
      const anchor = anchorRef.current.parentElement.getBoundingClientRect()
      const width = pickerRef.current ? pickerRef.current.offsetWidth : 248
      const height = pickerRef.current ? pickerRef.current.offsetHeight : 220
      const viewportGap = 8
      const pickerGap = 8
      const statusRow = anchorRef.current.closest('.manage-modal__row')
      const statusRowBounds = statusRow ? statusRow.getBoundingClientRect() : null
      let top = statusRowBounds ? statusRowBounds.top - pickerGap : anchor.bottom + pickerGap
      const preferredLeft = statusRowBounds ? anchor.left - 14 : anchor.left
      const left = Math.max(
        viewportGap,
        Math.min(preferredLeft, window.innerWidth - width - viewportGap)
      )

      if (top + height > window.innerHeight - viewportGap && anchor.top - height - pickerGap >= viewportGap) {
        top = anchor.top - height - pickerGap
      }
      setPosition({ top, left, visible: true })
    }

    updatePosition()
    const frame = window.requestAnimationFrame(updatePosition)
    window.addEventListener('resize', updatePosition)
    document.addEventListener('scroll', updatePosition, true)
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', updatePosition)
      document.removeEventListener('scroll', updatePosition, true)
    }
  }, [])

  const picker = (
    <div
      ref={pickerRef}
      className='manage-modal__colour-picker'
      role='dialog'
      aria-label={title}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        visibility: position.visible ? 'visible' : 'hidden'
      }}
    >
      <p className='manage-modal__colour-picker-title'>{title}</p>
      <div className='manage-modal__colour-grid'>
        {MANAGE_COLOURS.map(colour => (
          <button
            type='button'
            key={colour}
            className={`manage-modal__colour-option ${selected && selected.toLowerCase() === colour.toLowerCase() ? 'is-selected' : ''}`}
            style={{ backgroundColor: colour }}
            onClick={() => onSelect(colour)}
            aria-label={colour}
          />
        ))}
      </div>
      {note && <p className='manage-modal__colour-picker-note'>{note}</p>}
    </div>
  )

  return (
    <>
      <span ref={anchorRef} aria-hidden='true' />
      {ReactDOM.createPortal(picker, document.body)}
    </>
  )
}
